import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  useTheme,
  alpha,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Case, Payment } from '../../../types';
import { updatePayment } from '../../../services/firebase';
import { formatCurrency } from '../../../utils/formatters';
import { toast } from 'react-toastify';

interface EditPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  caseData: Case;
  payment: Payment;
  onPaymentUpdated: () => void;
}

const EditPaymentDialog: React.FC<EditPaymentDialogProps> = ({
  open,
  onClose,
  caseData,
  payment,
  onPaymentUpdated,
}) => {
  const theme = useTheme();
  const [newAmount, setNewAmount] = useState(payment.amount);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPaidExcludingCurrent = caseData.payments
    .filter(p => p.id !== payment.id)
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      const newTotal = totalPaidExcludingCurrent + Number(newAmount);
      if (newTotal > caseData.amountCharged) {
        setError('Total payments cannot exceed the case amount');
        return;
      }

      await updatePayment(caseData.id, payment.id, Number(newAmount));
      toast.success('Payment amount updated successfully');
      onPaymentUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating payment amount:', err);
      setError('Failed to update payment amount');
      toast.error('Failed to update payment amount');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          bgcolor: 'background.paper',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      <DialogTitle>
        Edit Payment Amount
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Payment Date: {new Date(payment.date).toLocaleDateString()}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Amount: {formatCurrency(payment.amount)}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Case Total Amount: {formatCurrency(caseData.amountCharged)}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Total Paid (excluding this payment): {formatCurrency(totalPaidExcludingCurrent)}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="New Amount"
          type="number"
          value={newAmount}
          onChange={(e) => setNewAmount(Number(e.target.value))}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: 'background.paper',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.2),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {newAmount !== payment.amount && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              New Total Paid will be: {formatCurrency(totalPaidExcludingCurrent + Number(newAmount))}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              New Balance will be: {formatCurrency(caseData.amountCharged - (totalPaidExcludingCurrent + Number(newAmount)))}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 100,
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            isSubmitting ||
            newAmount === payment.amount ||
            totalPaidExcludingCurrent + Number(newAmount) > caseData.amountCharged
          }
          sx={{
            minWidth: 100,
            bgcolor: theme.palette.success.main,
            '&:hover': {
              bgcolor: theme.palette.success.dark,
              boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.25)}`,
            },
          }}
        >
          {isSubmitting ? 'Updating...' : 'Update Amount'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPaymentDialog; 